import { useDeepCompareEffect, useURLParams } from "@whitespace/gatsby-hooks";
import React, { createContext, useContext } from "react";

import { useSearchBackend } from "./useSearchBackend";
import { useSearchParams } from "./useSearchParams";

export const siteSearchContext = createContext({ setQuery: () => {} });

export const DEFAULT_SORT_OPTIONS = {
  relevance: {
    name: "Relevans",
    field: false,
    order: "desc",
  },
  date: {
    name: "Datum",
    field: "post_date",
    order: "desc",
  },
  title: {
    name: "Titel",
    field: "post_title",
    order: "asc",
  },
};

export const DEFAULT_FILTERS = {
  contentType: {
    // param: "contentType",
    // toRequestParams(value) {
    //   return { contentType: value };
    // },
    // options: {
    //   foo: {
    //     label: "Foo",
    //   },
    //   bar: {
    //     label: "Bar",
    //   },
    // },
  },
};

export function SiteSearchPageProvider({
  page: { urlPattern = "/search" },
  ...props
}) {
  const useSearchStateHandler = () => useURLParams(urlPattern);
  return (
    <SearchHandler useSearchStateHandler={useSearchStateHandler} {...props} />
  );
}

export function SearchHandler({
  sortOptions = DEFAULT_SORT_OPTIONS,
  filters = DEFAULT_FILTERS,
  defaultSort = "relevance",
  hitsPerPage = 10,
  children,
}) {
  let [params, setParams] = useSearchParams({});
  let { page = 0, query = "", sort = defaultSort, ...filterValues } = params;
  page = Number(page);

  let [
    { result, isPending, error, isEmptyRequest },
    setRequest,
  ] = useSearchBackend();

  useDeepCompareEffect(() => {
    const { field, order } = sortOptions[sort];
    const paramsFromFilters = {};
    Object.entries(filters).forEach(([key, { toRequestParams, param }]) => {
      let value = filterValues[key];
      Object.assign(
        paramsFromFilters,
        toRequestParams ? toRequestParams(value) : { [param || key]: value },
      );
    });
    let request = {
      ...paramsFromFilters,
      query,
      sortBy: field,
      sortOrder: order,
      from: page * hitsPerPage,
      size: hitsPerPage,
    };
    setRequest(
      // Don’t make any request if query and params are empty
      Object.values(paramsFromFilters).some((param) => param != null) || query
        ? request
        : null,
    );
  }, [
    filters,
    filterValues,
    hitsPerPage,
    page,
    query,
    setRequest,
    sort,
    sortOptions,
  ]);

  const { Provider } = siteSearchContext;

  const setSort = (sort) => {
    setParams((params) => ({
      ...params,
      sort,
      page: undefined,
    }));
  };

  const setQuery = (query) => {
    setParams((params) => ({
      ...params,
      query,
      page: undefined,
    }));
  };

  const setFilterValues = (newValues) => {
    if (typeof newValues === "function") {
      newValues = newValues(filterValues);
    }
    setParams((params) => ({
      ...params,
      ...newValues,
      page: undefined,
    }));
  };

  const { hits, total = 0, aggregations = {} } = result || {};

  const pages = Math.ceil(total / hitsPerPage);

  return (
    <Provider
      value={{
        // Config:
        sortOptions,

        // Input:
        query,
        setQuery,
        page,
        sort,
        setSort,
        isEmptyRequest,

        // Filters:
        filters,
        filterValues,
        setFilterValues,

        // Status:
        isPending,
        error,

        // Results:
        hits,
        pages,
        total,
        aggregations,
      }}
    >
      {children}
    </Provider>
  );
}

export function useSiteSearch() {
  return useContext(siteSearchContext);
}

function getFilterOptions({ aggregations, filter }) {
  return aggregations?.[filter] || {};
}

export function useSiteSearchFilter(filter) {
  const { filters, filterValues, setFilterValues, aggregations } = useContext(
    siteSearchContext,
  );
  const filterValue = filterValues[filter];
  const filterConfig = filters[filter];
  const options =
    filterConfig.options ||
    (filterConfig.getOptions || getFilterOptions)({
      aggregations,
      filter,
      ...filterConfig,
    });
  return {
    ...filterConfig,
    options,
    filterValue,
    setFilterValue: (newValue) => {
      if (typeof newValue === "function") {
        newValue = newValue(filterValue);
      }
      setFilterValues((values) => ({
        ...values,
        [filter]: newValue,
      }));
    },
  };
}
